import { PlaneGeometry, MeshBasicMaterial, Color, Mesh, Group } from "three";
import { OVERLAY_PLANE_GROUP_Z } from "./constants.js";

class OverlayPlanes {
	blurPlane = null;
	overlayPlane = null;
	overlayPlaneGroup = null; // Group containing both blur and overlay planes

	setup(camera, canvasRect, finalBlurTexture, foregroundLayer) {
		// Create blur plane upfront to prevent pop-in
		// Calculate plane size to fill camera view at overlay plane group position
		// Distance from camera to overlay plane group (camera is at positive Z, group is at negative Z)
		const distance = camera.position.z - OVERLAY_PLANE_GROUP_Z;
		const fov = camera.fov * (Math.PI / 180);
		const planeHeight = 2 * Math.tan(fov / 2) * distance;
		const aspect = canvasRect.width / canvasRect.height;
		const planeWidth = planeHeight * aspect;

		const blurPlaneGeometry = new PlaneGeometry(planeWidth, planeHeight);
		const blurPlaneMaterial = new MeshBasicMaterial({
			map: finalBlurTexture,
			transparent: true,
			opacity: 1,
		});
		this.blurPlane = new Mesh(blurPlaneGeometry, blurPlaneMaterial);
		// Position at z=0 where the coins orbit, facing the camera
		this.blurPlane.position.set(0, 0, 0);
		this.blurPlane.lookAt(camera.position);
		// Set to foreground layer so it renders with the winning coin
		this.blurPlane.layers.set(foregroundLayer);

		// Create overlay plane with the specified color
		const overlayPlaneGeometry = new PlaneGeometry(planeWidth, planeHeight);
		const overlayPlaneMaterial = new MeshBasicMaterial({
			color: new Color(0x7994ff), // rgba(121, 148, 255, ...) in hex
			transparent: true,
			opacity: 0, // Start with 0 opacity, will be updated based on blur amount
		});
		this.overlayPlane = new Mesh(overlayPlaneGeometry, overlayPlaneMaterial);
		// Position slightly in front of blur plane to ensure it renders on top
		this.overlayPlane.position.set(0, 0, 0.01);
		this.overlayPlane.lookAt(camera.position);
		// Set to foreground layer so it renders with the winning coin
		this.overlayPlane.layers.set(foregroundLayer);

		// Create a group to hold both planes
		this.overlayPlaneGroup = new Group();
		this.overlayPlaneGroup.add(this.blurPlane);
		this.overlayPlaneGroup.add(this.overlayPlane);
		// Position group behind coin so coin can pass in front during exit animation
		this.overlayPlaneGroup.position.set(0, 0, OVERLAY_PLANE_GROUP_Z);
		// Start scaled down to very small (effectively invisible but still in scene)
		this.overlayPlaneGroup.scale.set(0.001, 0.001, 0.001);
	}

	updateSize(camera, canvasRect) {
		if (!this.blurPlane || !this.blurPlane.geometry) {
			return;
		}

		// Calculate plane size to fill camera view at overlay plane group position
		const distance = camera.position.z - OVERLAY_PLANE_GROUP_Z;
		const fov = camera.fov * (Math.PI / 180);
		const height = 2 * Math.tan(fov / 2) * distance;
		const aspect = canvasRect.width / canvasRect.height;
		const width = height * aspect;

		this.blurPlane.geometry.dispose();
		this.blurPlane.geometry = new PlaneGeometry(width, height);
		// Update plane orientation
		this.blurPlane.lookAt(camera.position);

		if (this.overlayPlane && this.overlayPlane.geometry) {
			this.overlayPlane.geometry.dispose();
			this.overlayPlane.geometry = new PlaneGeometry(width, height);
			// Update plane orientation
			this.overlayPlane.lookAt(camera.position);
		}
	}

	updateOrientation(camera) {
		if (this.blurPlane) {
			this.blurPlane.position.set(0, 0, 0);
			this.blurPlane.lookAt(camera.position);
		}
		if (this.overlayPlane) {
			this.overlayPlane.position.set(0, 0, 0.01);
			this.overlayPlane.lookAt(camera.position);
		}
	}

	setBlurAmount(blurAmount) {
		// Update overlay plane opacity based on blur amount
		// Map blur amount (0-1) to opacity (0 to 0.30)
		if (this.overlayPlane && this.overlayPlane.material) {
			this.overlayPlane.material.opacity = blurAmount * 0.30;
		}
	}

	setScale(scale) {
		if (this.overlayPlaneGroup) {
			this.overlayPlaneGroup.scale.set(scale, scale, scale);
		}
	}

	updateTexture(blurredTexture) {
		if (this.blurPlane && this.blurPlane.material) {
			this.blurPlane.material.map = blurredTexture;
			this.blurPlane.material.needsUpdate = true;
		}
	}
}

export default OverlayPlanes;

